import { InlineIcon } from "@iconify/react";

interface Subagent {
  id: number;
  label: string;
  value: string;
  name: string;
  email: string;
  legal_title: string;
}

interface SubagentSelectorProps {
  isDarkMode: boolean;
  subagents: Subagent[];
  isLoading: boolean;
  selectedSubagent: string | null;
  onSubagentChange: (value: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export function SubagentSelector({
  isDarkMode,
  subagents,
  isLoading,
  selectedSubagent,
  onSubagentChange,
  searchQuery,
  onSearchChange,
}: SubagentSelectorProps) {
  return (
    <div
      className={[
        "rounded-lg",
        "shadow-md",
        "border",
        "overflow-hidden",
        isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200",
      ].join(" ")}
    >
      <div
        className={[
          "px-4",
          "py-3",
          "border-b",
          isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-200",
        ].join(" ")}
      >
        <h3
          className={`text-lg! font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
        >
          Select Sub-agent
        </h3>
      </div>
      <div className="p-4 space-y-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search sub-agents..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className={[
              "w-full",
              "px-3",
              "py-2",
              "pr-10",
              "rounded-md",
              "border",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-blue-500",
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-400"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500",
            ].join(" ")}
          />
          <InlineIcon
            icon="mdi:magnify"
            className={[
              "absolute",
              "right-3",
              "top-1/2",
              "-translate-y-1/2",
              "w-5",
              "h-5",
              isDarkMode ? "text-gray-400" : "text-gray-500",
            ].join(" ")}
          />
        </div>
        {isLoading ? (
          <p
            className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Loading sub-agents...
          </p>
        ) : subagents.length === 0 ? (
          <p
            className={`text-sm text-center py-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {searchQuery ? "No sub-agents found" : "No sub-agents available"}
          </p>
        ) : (
          <div
            className={[
              "max-h-64",
              "overflow-y-auto",
              "border",
              "rounded-md",
              isDarkMode ? "border-gray-600" : "border-gray-200",
            ].join(" ")}
          >
            {subagents.map((subagent) => (
              <button
                key={subagent.id}
                type="button"
                onClick={() => {
                  onSubagentChange(selectedSubagent === subagent.value ? null : subagent.value);
                }}
                className={[
                  "w-full",
                  "px-3",
                  "py-2",
                  "text-left",
                  "border-b",
                  "last:border-b-0",
                  "transition-colors",
                  "hover:bg-opacity-50",
                  selectedSubagent === subagent.value
                    ? isDarkMode
                      ? "bg-blue-600 bg-opacity-30 text-blue-200"
                      : "bg-blue-100 text-blue-900"
                    : isDarkMode
                      ? "hover:bg-gray-800 text-gray-200 border-gray-600"
                      : "hover:bg-gray-100 text-gray-900 border-gray-200",
                ].join(" ")}
              >
                <div className="text-sm font-medium">{subagent.name || "N/A"}</div>
                {subagent.legal_title && (
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {subagent.legal_title}
                  </div>
                )}
                <div
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  {subagent.email}
                </div>
              </button>
            ))}
          </div>
        )}
        {selectedSubagent && (
          <div
            className={[
              "px-3",
              "py-2",
              "rounded-md",
              "text-sm",
              isDarkMode
                ? "bg-blue-600 bg-opacity-20 text-blue-200"
                : "bg-blue-50 text-blue-900",
            ].join(" ")}
          >
            Selected: {subagents.find((s) => s.value === selectedSubagent)?.label}
          </div>
        )}
      </div>
    </div>
  );
}

